import React, { useState } from 'react';
import { MessageCircle, X, Send, Loader2 } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/Card';
import { Button } from '../ui/Button';
import { Input } from '../ui/Input';
import { initiateChat } from '../../api/chatService';
import toast from 'react-hot-toast';
import authService from '../../api/authService';

/**
 * Customer Chat Request Component
 * Allows customers to initiate a chat session with an agent
 */
const CustomerChatRequest = ({ onChatInitiated }) => {
  const [showForm, setShowForm] = useState(false);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    guestName: '',
    email: '',
    subject: '',
    ticketNumber: ''
  });

  // Get current user info
  const currentUser = authService.getCurrentUser();
  const userName = sessionStorage.getItem('userName') || currentUser?.username || currentUser?.name || '';
  const userEmail = sessionStorage.getItem('userEmail') || currentUser?.email || '';

  // Pre-fill form with user data if available
  React.useEffect(() => {
    if (userName && !formData.guestName) {
      setFormData(prev => ({ ...prev, guestName: userName }));
    }
    if (userEmail && !formData.email) {
      setFormData(prev => ({ ...prev, email: userEmail }));
    }
  }, [userName, userEmail]);

  /**
   * Handle form submission
   */
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.guestName.trim()) {
      toast.error('Please enter your name');
      return;
    }

    setLoading(true);
    try {
      const response = await initiateChat({
        guestName: formData.guestName.trim(),
        email: formData.email?.trim() || null,
        subject: formData.subject?.trim() || 'General Inquiry',
        ticketNumber: formData.ticketNumber?.trim() || null
      });

      if (response.success && response.data) {
        toast.success('Chat request sent! An agent will join shortly.');
        setShowForm(false);
        // Reset form
        setFormData({
          guestName: userName || '',
          email: userEmail || '',
          subject: '',
          ticketNumber: ''
        });
        
        // Notify parent component
        if (onChatInitiated) {
          onChatInitiated(response.data);
        }
      } else {
        throw new Error(response.message || 'Failed to initiate chat');
      }
    } catch (error) {
      console.error('Error initiating chat:', error);
      toast.error(error.response?.data?.message || error.message || 'Failed to start chat. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  /**
   * Handle input change
   */
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  if (!showForm) {
    return (
      <Card className="cursor-pointer hover:shadow-lg transition-all duration-200 hover:border-blue-300"
            onClick={() => setShowForm(true)}>
        <CardContent className="p-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="h-12 w-12 rounded-full bg-blue-100 flex items-center justify-center">
                <MessageCircle className="h-6 w-6 text-blue-600" />
              </div>
              <div>
                <h3 className="font-semibold text-gray-900">Chat with Agent</h3>
                <p className="text-sm text-gray-600">Get instant help from our support team</p>
              </div>
            </div>
            <Button 
              variant="outline" 
              className="border-blue-300 text-blue-600 hover:bg-blue-50"
            >
              Start Chat
            </Button>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle className="flex items-center space-x-2">
            <MessageCircle className="h-5 w-5 text-blue-600" />
            <span>Start a Chat with Agent</span>
          </CardTitle>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setShowForm(false)}
            className="h-8 w-8 p-0"
          >
            <X className="h-4 w-4" />
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label htmlFor="guestName" className="block text-sm font-medium text-gray-700 mb-1">
              Your Name <span className="text-red-500">*</span>
            </label>
            <Input
              id="guestName"
              name="guestName"
              type="text"
              value={formData.guestName}
              onChange={handleChange}
              placeholder="Enter your name"
              required
              className="w-full"
            />
          </div>

          <div>
            <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
              Email (Optional)
            </label>
            <Input
              id="email"
              name="email"
              type="email"
              value={formData.email}
              onChange={handleChange}
              placeholder="your.email@example.com"
              className="w-full"
            />
          </div>

          <div>
            <label htmlFor="subject" className="block text-sm font-medium text-gray-700 mb-1">
              Subject (Optional)
            </label>
            <Input
              id="subject"
              name="subject"
              type="text"
              value={formData.subject}
              onChange={handleChange}
              placeholder="What do you need help with?"
              className="w-full"
            />
          </div>

          <div>
            <label htmlFor="ticketNumber" className="block text-sm font-medium text-gray-700 mb-1">
              Ticket Number (Optional)
            </label>
            <Input
              id="ticketNumber"
              name="ticketNumber"
              type="text"
              value={formData.ticketNumber}
              onChange={handleChange}
              placeholder="TKT-12345"
              className="w-full"
            />
          </div>

          <div className="flex items-center gap-2 pt-2">
            <Button
              type="submit"
              disabled={loading || !formData.guestName.trim()}
              className="flex-1 bg-blue-600 hover:bg-blue-700 text-white"
            >
              {loading ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Starting Chat...
                </>
              ) : (
                <>
                  <Send className="h-4 w-4 mr-2" />
                  Start Chat
                </>
              )}
            </Button>
            <Button
              type="button"
              variant="outline"
              onClick={() => setShowForm(false)}
              disabled={loading}
            >
              Cancel
            </Button>
          </div>
        </form>
      </CardContent>
    </Card>
  );
};

export default CustomerChatRequest;
